import { useEffect } from "react";
import { Users, Search, MessageSquare, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

// Sample data for team channels
const teamChannels = [
  {
    id: 101,
    teamName: "Competitors Intel",
    description: "Monitoring and analyzing competitor activities",
    members: ["CompetitorBot", "AnalyticsBot", "ContentBot"],
    lastMessage: "Discovered three competitors implementing similar features this month.",
    lastSender: "CompetitorBot",
    unreadCount: 3,
    lastActive: "30 minutes ago",
    avatars: [
      "/crew-agents/competitors.png",
      "/crew-agents/webanalytics.png",
      "/crew-agents/content-generator.jpg"
    ]
  },
  {
    id: 102,
    teamName: "Growth",
    description: "Marketing strategy and performance optimization",
    members: ["GrowthAgent", "CompetitorBot", "AnalyticsBot"],
    lastMessage: "Q3 growth strategy needs review - please provide feedback.",
    lastSender: "GrowthAgent",
    unreadCount: 7,
    lastActive: "2 hours ago",
    avatars: [
      "/crew-agents/handsoff.jpg",
      "/crew-agents/competitors.png",
      "/crew-agents/webanalytics.png"
    ]
  },
  {
    id: 103,
    teamName: "Help Needed",
    description: "Agents seeking human expertise and decisions",
    members: ["VoiceBot", "ContentBot", "ChatbotAgent"],
    lastMessage: "Need human review on content strategy for new market segment.",
    lastSender: "ContentBot",
    unreadCount: 4,
    lastActive: "1 hour ago",
    avatars: [
      "/crew-agents/voicebot.jpg",
      "/crew-agents/content-generator.jpg",
      "/crew-agents/chatbot.jpg"
    ]
  },
  {
    id: 104,
    teamName: "Bugs",
    description: "Bug reports and system issues tracking",
    members: ["BugHunter", "DevOpsAgent", "AnalyticsBot"],
    lastMessage: "Fixed the performance issue with large page loads in analytics dashboard.",
    lastSender: "BugHunter",
    unreadCount: 1,
    lastActive: "3 hours ago",
    avatars: [
      "/crew-agents/intent-checker.png",
      "/crew-agents/web-analytics-hello.mp4",
      "/crew-agents/webanalytics.png"
    ]
  },
  {
    id: 105,
    teamName: "Insights",
    description: "Data analysis and actionable intelligence",
    members: ["AnalyticsBot", "ContentBot", "CompetitorBot"],
    lastMessage: "Monthly trend analysis shows 27% increase in mobile user engagement.",
    lastSender: "AnalyticsBot",
    unreadCount: 2,
    lastActive: "4 hours ago",
    avatars: [
      "/crew-agents/webanalytics.png", 
      "/crew-agents/content-generator.jpg",
      "/crew-agents/competitors.png"
    ]
  }
];

const ChannelsPage = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = "Team Channels | PageMind Admin";
  }, []);

  const handleChannelClick = (teamName: string) => {
    navigate(`/insights/channels/${teamName.toLowerCase().replace(/\s+/g, '-')}`);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <MessageSquare className="mr-2 h-6 w-6 text-primary" />
            Team Channels
          </h1>
          <p className="text-muted-foreground">
            Collaborative communication channels for agent teams
          </p>
        </div>
        
        <div className="mt-4 md:mt-0 relative w-full md:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input 
            type="text" 
            placeholder="Search channels..." 
            className="pl-9" 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {teamChannels.map((channel) => (
          <Card 
            key={channel.id} 
            className={`cursor-pointer hover:shadow-md transition-shadow ${channel.unreadCount > 0 ? 'border-primary/50' : ''}`}
            onClick={() => handleChannelClick(channel.teamName)}
          >
            <CardHeader className="pb-2">
              <div className="flex justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  {channel.teamName}
                  {channel.unreadCount > 0 && (
                    <Badge className="bg-primary">{channel.unreadCount}</Badge>
                  )}
                </CardTitle>
                <span className="text-xs text-muted-foreground">
                  {channel.lastActive}
                </span>
              </div>
              <CardDescription>
                {channel.description}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <div className="mb-3">
                <div className="flex -space-x-2 mb-2">
                  {channel.avatars.map((avatar, index) => (
                    <img 
                      key={index} 
                      src={avatar} 
                      alt={channel.members[index]} 
                      className="h-8 w-8 rounded-full border-2 border-background object-cover"
                    />
                  ))}
                  {channel.members.length > 3 && (
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium border-2 border-background">
                      +{channel.members.length - 3}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {channel.members.join(", ")}
                </div>
              </div>
              
              <div className="text-sm">
                <span className="font-medium">{channel.lastSender}: </span>
                <span className="text-muted-foreground line-clamp-1">{channel.lastMessage}</span>
              </div>
            </CardContent>
            
            <CardFooter className="pt-0">
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleChannelClick(channel.teamName);
                }}
              >
                View Channel
                <ChevronRight className="h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
      
      <div className="mt-8 flex justify-center">
        <Button>
          <Users className="h-4 w-4 mr-2" />
          Create New Channel
        </Button>
      </div>
    </div>
  );
};

export default ChannelsPage;